import * as TemplateTypes from 'types/template.types';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { deviceEvents } from 'util/device-util';
import { fromLocalStorage } from 'util/util';
import { AppState } from '@capacitor/app';
import { useGlobalStore } from './global';

const useAudioStore = defineStore('audio-store', () => {
    let persisted : TemplateTypes.AudioSettings = fromLocalStorage('audio-settings', {
        muted: false
    })

    const global = useGlobalStore();
    const muted = ref(persisted.muted);

    /**
     * Listen for device events change
     */
    deviceEvents.on('appStateChange', (state: AppState) => {
        let scene = global.sceneState.scene;
        if (!scene) return;
    
        if (!state.isActive){
            scene.game.sound.volume = 0;
            return;
        }
    
        if (!muted.value){
            scene.game.sound.volume = 1;
        }
    })

    const persist = () => {
        localStorage.setItem('audio-settings', JSON.stringify({
            muted: muted.value
        }))
    }

    const init = () => {
        if (muted.value){
            let scene = global.sceneState.scene;
            if (scene){
                scene.game.sound.volume = 0;
            }
        }
    }

    const setMuted = (m: boolean) => {
        muted.value = m;
        let scene = global.sceneState.scene;
        if (scene){
            scene.game.sound.volume = muted.value ? 0 : 1;
        }

        persist();
    }

    const play = (key: string, extra?: Phaser.Types.Sound.SoundConfig | Phaser.Types.Sound.SoundMarker | undefined) => {
        global.sceneState.scene?.sound.play(key, extra);
    }

    const add = (key: string, config?: Phaser.Types.Sound.SoundConfig | undefined) : Phaser.Sound.WebAudioSound => {
        return <Phaser.Sound.WebAudioSound> global.sceneState.scene?.sound.add(key, config);
    }

    const fadeIn = (audio: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound, duration: number = 500, onComplete?: Function) => {
        audio.volume = 0;
        audio.play();
        let tweenTarget = { volume: 0 }; //Use a proxy target because the tweens are too granular
        return new Promise((res, rej) => {
            global.sceneState.scene?.tweens.add({
                targets: tweenTarget,
                volume: 1,
                duration: duration,
                onUpdate: () => audio.volume = parseFloat(tweenTarget.volume.toFixed(2)),
                onComplete: () => {
                    onComplete?.(audio);
                    res(audio);
                }
            });
        })
    }

    const fadeOut = (audio: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound, duration: number = 500, onComplete?: Function) => {
        let tweenTarget = { volume: 1 }; //Use a proxy target because the tweens are too granular
        return new Promise((res, rej) => {
            global.sceneState.scene?.tweens.add({
                targets: tweenTarget,
                volume: 0,
                duration: duration,
                onUpdate: () => audio.volume = parseFloat(tweenTarget.volume.toFixed(2)),
                onComplete: () => {
                    onComplete?.(audio);
                    res(audio);
                }
            });
        })
    }

    return { init, muted, setMuted, play, add, fadeIn, fadeOut };
})

export { useAudioStore };
